<template>
   <div class="quote-wrap" :class="{active: isActive}" ref="quoteWrap">
      <div class="d-block quote-trigger" ref="quoteTrigger"></div>
      <blockquote class="quote" ref="quote">
         <div class="quote-bg" :style="{ borderColor: color }" ref="quoteBg"></div>
         <div class="quote-content d-flex flex-column align-items-center text-center">

            <StageQuote v-if="this.$store.state.isPortrait" :quote="quote" :article="article"></StageQuote>

            <span class="quote-heading">{{ heading }}</span>
            <span class="quote-subheading">&mdash; {{ subheading }}</span>

            <audio-player
                    v-if="audioFile && (!this.$store.state.isPortrait || isActive)"
                    :filename="audioFile"
                    :quoteActive="isActive"
                    :quoteIdx="index"
                    v-on:inactiveListenClick="handleListenClick"
            >
            </audio-player>
         </div>
      </blockquote>
   </div>
</template>

<script>
    import ScrollMagic from 'scrollmagic'
    import 'TweenMax'
    import 'TimelineMax'
    import 'animation.gsap'
    import 'debug.addIndicators'
    import {bus, smController, body} from '../consts'

    import AudioPlayer from './AudioPlayer.vue';
    import StageQuote from './StageQuote.vue';

    export default {
        components: {
            AudioPlayer, StageQuote
        },
        name: 'Quote',
        props: [
            'quote',
            'index',
            'activeQuoteIdx',
            'article',
            'quotesLength'
        ],
        data() {
            return {
                heading: this.quote.heading,
                subheading: this.quote.subheading,
                color: this.quote.color,
                audioFile: this.quote.audioFile,
                pinQuoteScene: new ScrollMagic.Scene(),
                quoteAnimToScene: new ScrollMagic.Scene(),
                quoteAnimFromScene: new ScrollMagic.Scene(),
                isActive: false,
                quoteBgToTween: {},
                quoteBgFromTween: {},
                mobilePadding: 8
            }
        },
        computed: {
            mobilePinOffset() {
                return (this.$store.state.headerHeight + this.mobilePadding) * -1;
            },
            quotePinDuration () {
                return window.matchMedia('max-width: 800px').matches ? 150 : 500;
            }
        },
        methods: {
            clearScenes() {
                this.pinQuoteScene.remove();
                this.quoteAnimToScene.remove();
                this.quoteAnimFromScene.remove();
            },
            activateQuote() {
                this.isActive = true;
                if (this.$store.state.isPortrait) {
                    this.$refs.quoteBg.style.backgroundColor = this.color;
                    this.$refs.quoteBg.classList.add('full-screen');
                    setTimeout(() => {
                        if (this.isActive) { // make sure it's STILL active
                            this.$refs.quote.classList.add('full-screen');
                        }
                    }, 500);
                } else {
                    bus.$emit('reachedQuote', this.index);
                }
            },
            deactivateQuote() {
                this.isActive = false;
                (this.index + 1) === this.quotesLength ? bus.$emit('endedQuote') : null;
                if (this.$store.state.isPortrait) {
                    this.$refs.quoteBg.style.backgroundColor = 'transparent';
                    this.$refs.quoteBg.classList.remove('full-screen');
                    this.$refs.quote.classList.remove('full-screen');
                }
            },
            handleListenClick(idx) {
                // modify duration if user clicked on a previous quote (due to the farther scroll distance)
                let duration = this.activeQuoteIdx > idx ? 1.2 : 0.4;

                // set scrollTo animation
                smController.scrollTo((distance) => {
                    TweenMax.to(window, duration, {scrollTo: distance});
                });

                // scroll to ScrollMagic Scene that activates the quote
                smController.scrollTo(this.quoteAnimToScene);
            },
            handleResize() {

                if (!this.$store.state.isPortrait) {
                  // create new Tweens so that the width vars can update to reflect the resized window
                  this.quoteBgToTween = {};
                  this.quoteBgFromTween = {};
                  this.setDesktopTweens();
                  // update the Scenes to use the new Tweens
                  this.quoteAnimToScene.setTween(this.quoteBgToTween);
                  this.quoteAnimFromScene.setTween(this.quoteBgFromTween);
                }

                if (this.$refs.quoteBg) {

                    // adjust the quote background width as necessary on resize
                    if (this.isActive) {
                        TweenMax.to(this.$refs.quoteBg, 0.3, {width: this.$el.offsetWidth});
                    }
                }
            },
            getMobileQuoteActiveHeight() {
                return window.innerHeight - (this.mobilePadding * 2) - this.$store.state.headerHeight;
            },
            setPortraitScenes() {

                this.pinQuoteScene
                    .triggerHook('onLeave')
                    .triggerElement(this.$refs.quoteTrigger)
                    .offset(this.mobilePinOffset)
                    .duration(this.getMobileQuoteActiveHeight())
                    .setPin(this.$refs.quote)
                    .addTo(smController)
                //.addIndicators({name: 'quotePin', colorEnd: 'black'})

                this.quoteAnimToScene
                    .triggerHook('onLeave')
                    .triggerElement(this.$refs.quoteTrigger)
                    .offset(this.mobilePinOffset)
                    .on('start', (e) => {
                        if (e.scrollDirection === 'REVERSE') {
                            this.deactivateQuote();
                        } else if (e.scrollDirection === 'FORWARD') {
                            this.activateQuote();
                        }
                    })
                    .addTo(smController)
                //.addIndicators({name: 'quoteToAnim', colorEnd: 'black'})

                this.quoteAnimFromScene
                    .triggerHook('onLeave')
                    .triggerElement(this.$refs.quoteTrigger)
                    .offset(this.mobilePinOffset + this.getMobileQuoteActiveHeight())
                    .on('start', (e) => {
                        if (e.scrollDirection === 'FORWARD') {
                            this.deactivateQuote();
                        } else if (e.scrollDirection === 'REVERSE') {
                            this.activateQuote();
                        }
                    })
                    .addTo(smController)
                //.addIndicators({name: 'quoteFromAnim', colorEnd: 'black'})
            },
            setDesktopScenes() {

                this.pinQuoteScene
                    .triggerElement(this.$refs.quoteTrigger)
                    .offset(this.$refs.quote.offsetHeight / 2)
                    .duration(this.quotePinDuration)
                    .setPin(this.$refs.quote)
                    .addTo(smController)
                // .addIndicators({name:'quotePin'})

                this.quoteAnimToScene
                    .triggerElement(this.$refs.quoteTrigger)
                    .offset(this.$refs.quote.offsetHeight / 2)
                    .setTween(this.quoteBgToTween)
                    .on('start', (e) => {
                        if (e.scrollDirection === 'REVERSE') {
                            this.deactivateQuote();
                        } else if (e.scrollDirection === 'FORWARD') {
                            this.activateQuote();
                        }
                    })
                    .addTo(smController)
                // .addIndicators({name:'quoteToAnim'})

                this.quoteAnimFromScene
                    .triggerElement(this.$refs.quoteTrigger)
                    .offset((this.$refs.quote.offsetHeight / 2) + this.quotePinDuration)
                    .setTween(this.quoteBgFromTween)
                    .on('start', (e) => {
                        if (e.scrollDirection === 'FORWARD') {
                            this.deactivateQuote();
                        } else if (e.scrollDirection === 'REVERSE') {
                            this.activateQuote();
                        }
                    })
                    .addTo(smController)
                // .addIndicators({name:'quoteFromAnim'})
            },
            setDesktopTweens () {
              this.quoteBgToTween = TweenMax.fromTo(this.$refs.quoteBg, 0.5,
                {width: 250, backgroundColor: 'transparent', opacity: 0.2},
                {width: this.$el.offsetWidth, backgroundColor: this.color, opacity: 1, overwrite: false, immediateRender: false});
              this.quoteBgFromTween = TweenMax.fromTo(this.$refs.quoteBg, 0.5,
                {width: this.$el.offsetWidth, backgroundColor: this.color, opacity: 1},
                {width: 250, backgroundColor: 'transparent', opacity: 0.2, overwrite: false, immediateRender: false});
            }
        },
        mounted() {

            if (this.$store.state.isPortrait) {

                this.setPortraitScenes();

            } else {

                this.setDesktopTweens();
                this.setDesktopScenes();

                bus.$on('resizeComplete', this.handleResize);
            }
        },
        beforeDestroy() {
            this.clearScenes();
            if (!this.$store.state.isPortrait) {
                bus.$off('resizeComplete', this.handleResize);
            }
        }
    }
</script>

<style lang="scss">
   @import '../scss/the-goods';

   .quote {
      position : relative;
      margin   : 0 auto;

      @include screen-desktop {
         padding : 7.7rem 5rem 6.5rem 5rem;
      }

      .portrait-mode & {
         height          : calc(100vh - 66px);
         display         : flex;
         overflow        : hidden;
         flex-direction  : column;
         justify-content : center;
      }

      .landscape-mode & {
         padding : 4rem 4.4rem 3rem;
      }

      &-wrap {

         .landscape-mode & {
            margin : 0 auto 6.2rem auto;

            &:last-of-type {
               margin-bottom : 0;
            }
         }

         .portrait-mode & {
            overflow     : hidden;
            margin-left  : -1.7rem;
            margin-right : -1.7rem;
         }
      }

      &-bg {
         position     : absolute;
         top          : 0;
         left         : 50%;
         transform    : translateX(-50%);
         width        : 250px;
         height       : 100%;
         border-style : solid;
         border-width : 20px;
         opacity      : 0.2;

         .portrait-mode & {
            width            : 60%;
            height           : 60%;
            top              : 20%;
            left             : 20%;
            transform        : scale(1);
            transform-origin : center;
            transition       : background-color 600ms $default-bez, transform 600ms $default-bez, opacity 600ms $default-bez;

            &.full-screen {
               transform : scale(1.8);
               opacity   : 1;
            }
         }
      }

      &-content {
         position : relative;
         z-index  : 2;

         .portrait-mode & {
            transition : opacity 400ms $default-bez;
         }
      }

      &-heading {
         font-weight : 400;
         font-size   : 3.6rem;
         line-height : 1.25;

         .landscape-mode & {
            @include screen-xs-sm {
               font-size   : 2.5rem;
               line-height : 1;
            }
         }

         @include screen-xs-sm {
            font-size      : 3rem;
            line-height    : 3.7rem;
            letter-spacing : 0.093rem;
         }

         @include screen-xxl {
            font-size : 4rem;
         }
      }

      &-subheading {
         font-size   : 1.6rem;
         line-height : 1.2;
         margin      : 1.5rem 0 0 0;

         .landscape-mode & {
            @include screen-xs-sm {
               font-size : 1.4rem;
            }
         }

         @include screen-xs-sm {
            font-size      : 1.2rem;
            line-height    : 1.9rem;
            letter-spacing : 0.038rem;
         }

         @include screen-xxl {
            font-size : 2rem;
         }
      }
   }

   .quote-wrap.active {

      .quote-content {
         .portrait-mode & {
            opacity : 0;

            .stage-quote-wrap {
               opacity : 0;
            }
         }
      }

      .quote-heading, .quote-subheading {
         transition : color 0.3s ease-in-out;

         .landscape-mode & {
            color : white;
         }
      }
   }

   .quote.full-screen {

      .quote-content {
         position : absolute;
         height   : 100%;
         width    : 100%;
         opacity  : 1 !important;

         .quote-heading, .quote-subheading {
            display : none;
         }

         .stage-quote-wrap {
            opacity : 1 !important;
         }

         .stage-quote-img-wrap img {
            transform : translateX(0%);
         }

         .stage-quote-text p,
         .stage-quote-author span,
         .stage-quote-logo img {
            transform : translateY(0%);
         }
      }

      .audio-player {
         opacity : 1;
      }
   }

</style>



// WEBPACK FOOTER //
// src/components/Quote.vue